import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth, MfaChallenge, LoginOtpChallenge } from '../contexts/AuthContext';
import { useProgramThemeSettings } from '../contexts/ProgramThemeContext';
import { useAppThemeSettings } from '../contexts/AppThemeContext';
import { legalPageService } from '../services/legalPageService';
import { loginPageConfigService } from '../services/loginPageConfigService';
import { programService } from '@/modules/programs/services/programService';
import { resolveDefaultProgramRouteFromApi } from '@/modules/programs/services/resolveDefaultProgramRouteService';
import { getProgramAuthMode } from '../../programs/services/programAuthSettingsService';
import { appRoutes, programRoutes } from '../constants/routes';
import { resolveDashboardLandingPath } from '@/modules/programs/dashboard-governance/DashboardLandingResolver';
import LoginForm from '../components/login/LoginForm';
import LoginTemplateRenderer from '../components/login/LoginTemplateRenderer';
import MfaVerifyStep from '../components/login/MfaVerifyStep';
import OtpLoginStep from '../components/login/OtpLoginStep';
import LoginOtpVerifyStep from '../components/login/LoginOtpVerifyStep';
import LoginPageSkeleton from '../components/login/LoginPageSkeleton';
import type { LoginTemplateConfig, LoginTemplateProps } from '../types/loginTemplate';
import { getSelfRegistrationConfig, getSelfRegistrationConfigByUrl } from '../services/selfRegistrationService';
import { SelfRegistrationModal } from '../components/SelfRegistrationModal';
import { normalizeProgramAwareUrl, shouldShowSelfRegistrationOption } from '../utils/programUrlVisibility';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { programCode: urlProgramCode } = useParams<{ programCode?: string }>();
  const { isAuthenticated, user, login: authLogin, verifyMfa, verifyLoginOtp, resendLoginOtp, sendOtp, otpLogin } = useAuth();
  const themeSettings = useProgramThemeSettings();
  const appThemeSettings = useAppThemeSettings();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [rememberMe] = useState(false);

  // MFA / OTP state
  type LoginStep = 'password' | 'otp' | 'mfa' | 'loginOtp';
  const [step, setStep] = useState<LoginStep>('password');
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null);
  const [loginOtpChallenge, setLoginOtpChallenge] = useState<LoginOtpChallenge | null>(null);
  const [mfaError, setMfaError] = useState('');

  const shouldHonorReturnPath = (candidate?: string, resolvedProgramRoute?: string) => {
    if (!candidate?.trim()) {
      return false;
    }

    const normalizedCandidate = candidate.trim();

    if (
      normalizedCandidate === appRoutes.login() ||
      normalizedCandidate === appRoutes.programSelection() ||
      normalizedCandidate === appRoutes.dashboard() ||
      normalizedCandidate === appRoutes.superadminDashboard()
    ) {
      return false;
    }

    if (/^\/programs\/[^/]+\/(workspace|dashboard)\/?$/i.test(normalizedCandidate)) {
      return false;
    }

    if (resolvedProgramRoute && normalizedCandidate === resolvedProgramRoute) {
      return false;
    }

    return true;
  };

  const { data: loginLinksResponse } = useQuery({
    queryKey: ['legalPages', 'login-links', urlProgramCode],
    queryFn: () => legalPageService.getLoginLinks(urlProgramCode),
  });
  const loginLinks = loginLinksResponse?.data || [];

  // Fetch login page configuration from API
  const { data: loginConfigResponse, isLoading: configLoading } = useQuery({
    queryKey: ['loginPageConfig', urlProgramCode],
    queryFn: () => loginPageConfigService.getResolvedConfig(urlProgramCode),
    staleTime: 0, // Always check for fresh data
    refetchOnMount: 'always',
  });

  // Fetch program auth mode (PASSWORD/OTP/BOTH)
  const { data: authModeData } = useQuery({
    queryKey: ['programAuthMode', urlProgramCode],
    queryFn: () => getProgramAuthMode({ programCode: urlProgramCode }),
    enabled: !!urlProgramCode,
    staleTime: 60000,
  });
  const loginMode = authModeData?.data?.loginMode ?? 'PASSWORD';

  // Fetch self-registration config by route program code when available.
  const { data: selfRegConfigByCode } = useQuery({
    queryKey: ['selfRegistrationConfig', urlProgramCode],
    queryFn: () => getSelfRegistrationConfig(urlProgramCode!),
    enabled: !!urlProgramCode,
    staleTime: 60000,
  });

  // Root login fallback: resolve self-registration config by current browser origin.
  const { data: selfRegConfigByUrl } = useQuery({
    queryKey: ['selfRegistrationConfigByUrl', typeof window !== 'undefined' ? window.location.origin : null],
    queryFn: () => getSelfRegistrationConfigByUrl(window.location.origin),
    enabled: !urlProgramCode && typeof window !== 'undefined',
    staleTime: 60000,
  });

  const selfRegConfig = selfRegConfigByCode ?? selfRegConfigByUrl;
  const effectiveProgramCode = urlProgramCode ?? selfRegConfig?.programCode ?? null;

  const [selfRegModalOpen, setSelfRegModalOpen] = useState(false);
  const browserOrigin = typeof window !== 'undefined' ? window.location.origin : null;
  const selfRegistrationVisible = shouldShowSelfRegistrationOption({
    selfRegistrationEnabled: Boolean(selfRegConfig?.enabled),
    browserUrl: browserOrigin,
    configuredProgramUrl: selfRegConfig?.programUrl,
  });

  useEffect(() => {
    if (!selfRegConfig) {
      return;
    }

    const normalizedBrowserUrl = normalizeProgramAwareUrl(browserOrigin ?? '');
    const normalizedProgramUrl = normalizeProgramAwareUrl(selfRegConfig.programUrl ?? '');

    console.debug('[SelfRegistrationVisibility]', {
      routeProgramCode: urlProgramCode ?? null,
      resolvedProgramCode: selfRegConfig.programCode ?? null,
      effectiveProgramCode,
      enabled: selfRegConfig.enabled,
      browserUrl: browserOrigin,
      configuredProgramUrl: selfRegConfig.programUrl,
      normalizedBrowserUrl,
      normalizedProgramUrl,
      visible: selfRegistrationVisible,
    });
  }, [
    browserOrigin,
    effectiveProgramCode,
    selfRegConfig,
    selfRegistrationVisible,
    urlProgramCode,
  ]);

  // When programCode is present and mode is OTP-only, switch straight to OTP step
  useEffect(() => {
    if (loginMode === 'OTP' && step === 'password') {
      setStep('otp');
    }
  }, [loginMode, step]);

  /**
   * Resolves the post-login redirect using the centralized route resolver.
   * 
   * This function:
   * 1. Handles SuperAdmin redirect
   * 2. Fetches menu permissions from API if program is assigned
   * 3. Uses resolveDefaultProgramRoute to pick the best landing page
   * 4. Falls back to program selection if multiple programs
   * 5. Falls back to dashboard if no programs
   */
  const resolvePostLoginRedirect = async (loggedInUser: { role: string; programId?: string; programRoleName?: string; hospitalId?: string; dashboardKey?: string; landingRoute?: string }) => {
    // SuperAdmin - redirect to superadmin dashboard
    if (loggedInUser.role === 'SuperAdmin' || loggedInUser.role === 'ProductAdmin') {
      console.debug('[Login Redirect] GlobalAdmin → /superadmin/dashboard');
      return appRoutes.superadminDashboard();
    }

    // User has a pre-assigned program in their profile
    if (loggedInUser.programId) {
      sessionStorage.setItem('epprem_selected_program', loggedInUser.programId);

      console.debug('[Login Redirect] Resolving route for program user', {
        programId: loggedInUser.programId,
        programRoleName: loggedInUser.programRoleName,
        dashboardKey: loggedInUser.dashboardKey,
        landingRoute: loggedInUser.landingRoute,
      });

      // Priority 1: Use landing route from login response (already resolved by backend)
      if (loggedInUser.landingRoute?.trim()) {
        console.debug('[Login Redirect] ✓ Priority 1 – Using login response landingRoute:', loggedInUser.landingRoute);
        return loggedInUser.landingRoute;
      }

      // Priority 2: Resolve via dashboard landing path if dashboardKey available
      if (loggedInUser.dashboardKey?.trim()) {
        const resolved = resolveDashboardLandingPath({
          programId: loggedInUser.programId,
          dashboardKey: loggedInUser.dashboardKey,
          landingRoute: loggedInUser.landingRoute,
        });
        console.debug('[Login Redirect] ✓ Priority 2 – Using dashboardKey resolution:', { dashboardKey: loggedInUser.dashboardKey, resolved });
        return resolved;
      }

      // Priority 3: Fetch from /my-access API (legacy fallback)
      console.debug('[Login Redirect] ⚠ Priority 3 – Falling back to /my-access API');
      try {
        const defaultRoute = await resolveDefaultProgramRouteFromApi(loggedInUser.programId);
        console.debug('[Login Redirect] ✓ API resolved route:', defaultRoute);
        return defaultRoute;
      } catch (error) {
        console.error('[Login Redirect] ✗ API fallback failed:', error);
        return resolveDashboardLandingPath({
          programId: loggedInUser.programId,
          dashboardKey: loggedInUser.dashboardKey,
          landingRoute: loggedInUser.landingRoute,
        });
      }
    }

    // Fetch all accessible programs
    try {
      const myProgramsResponse = await programService.getMyPrograms();
      const activePrograms = (myProgramsResponse.data || []).filter((program) => program.status === 'ACTIVE');

      // No programs - show program selection (will display appropriate message)
      if (activePrograms.length === 0) {
        return appRoutes.programSelection();
      }

      // Check if we have a previously selected program still accessible
      const storedProgramId = sessionStorage.getItem('epprem_selected_program');
      if (storedProgramId) {
        const storedProgram = activePrograms.find((program) => program.programId === storedProgramId);
        if (storedProgram) {
          try {
            const defaultRoute = await resolveDefaultProgramRouteFromApi(storedProgram.programId);
            return defaultRoute;
          } catch (error) {
            console.error('Failed to resolve program route for stored program:', error);
          }
        }
      }

      // Exactly one program - auto-select it
      if (activePrograms.length === 1) {
        const selectedProgram = activePrograms[0];
        sessionStorage.setItem('epprem_selected_program', selectedProgram.programId);
        try {
          const defaultRoute = await resolveDefaultProgramRouteFromApi(selectedProgram.programId);
          return defaultRoute;
        } catch (error) {
          console.error('Failed to resolve program route for single program:', error);
          // Fallback to workspace only when API is unreachable
          return programRoutes.workspace(selectedProgram.programId);
        }
      }

      // Multiple programs - show selection screen
      return appRoutes.programSelection();
    } catch (error) {
      console.error('Failed to fetch programs:', error);
      return appRoutes.programSelection();
    }
  };

  // Determine if we're on a program-specific login page
  const isProgramLogin = !!urlProgramCode || themeSettings.hasCustomSettings;

  // Get the intended destination — from React Router state (ProtectedRoute) OR
  // from the ?returnUrl= query param (set by apiClient 401 interceptor)
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get('returnUrl');
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || returnUrl || undefined;

  // Redirect if already authenticated on page load (not after form submit)
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    // Skip if we're in a login/redirect process or user just submitted the form
    if (loading || isRedirecting || hasSubmitted) return;
    
    if (isAuthenticated && user) {
      setIsRedirecting(true);
      resolvePostLoginRedirect(user)
        .then((defaultRedirect) => {
          const isProgramRoleRedirect = !!user.programRoleName && !!user.programId;
          const redirectTo =
            isProgramRoleRedirect ||
            defaultRedirect === appRoutes.programSelection() ||
            !shouldHonorReturnPath(from, defaultRedirect)
              ? defaultRedirect
              : from || defaultRedirect;
          navigate(redirectTo, { replace: true });
        })
        .catch(() => {
          setIsRedirecting(false);
          navigate(appRoutes.programSelection(), { replace: true });
        });
    }
  }, [navigate, isAuthenticated, user, from, loading, isRedirecting, hasSubmitted]);

  const doRedirect = async (loggedInUser: { role: string; programId?: string; programRoleName?: string; hospitalId?: string; dashboardKey?: string; landingRoute?: string }, showPageLoader = false) => {
    if (showPageLoader) {
      setIsRedirecting(true);
    }
    const defaultRedirect = await resolvePostLoginRedirect(loggedInUser);
    const isProgramRoleRedirect = !!loggedInUser.programRoleName && !!loggedInUser.programId;
    const redirectTo =
      isProgramRoleRedirect ||
      defaultRedirect === appRoutes.programSelection() ||
      !shouldHonorReturnPath(from, defaultRedirect)
        ? defaultRedirect
        : from || defaultRedirect;

    console.debug('[Login Redirect] Final navigation decision', {
      defaultRedirect,
      isProgramRoleRedirect,
      from,
      finalRedirectTo: redirectTo,
    });

    navigate(redirectTo, { replace: true });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);
    setHasSubmitted(true);

    try {
      const result = await authLogin(username, password, rememberMe);

      if (!result.success) {
        setError(result.message);
        setLoading(false);
        setHasSubmitted(false);
        return;
      }

      if (result.requiresMfa) {
        setMfaChallenge(result.mfaChallenge);
        setStep('mfa');
        setLoading(false);
        return;
      }

      if (result.requiresOtp) {
        setLoginOtpChallenge(result.otpChallenge);
        setStep('loginOtp');
        setLoading(false);
        return;
      }

      await doRedirect(result.user);
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setLoading(false);
      setHasSubmitted(false);
    }
  };

  // Show loading while redirecting authenticated user
  if (isRedirecting) {
    return <LoginPageSkeleton />;
  }

  // Show loading while fetching login page configuration
  if (configLoading) {
    return <LoginPageSkeleton />;
  }

  // Map API config to template config format
  const apiConfig = loginConfigResponse?.config;
  const templateConfig: LoginTemplateConfig = apiConfig
    ? {
        templateId: apiConfig.templateId,
        templateName: '',
        description: '',
        formPlacement: apiConfig.formPlacement,
        sections: apiConfig.sections,
        // Layout configuration for panel visibility
        layout: apiConfig.layout ? {
          showLeftPanel: apiConfig.layout.showLeftPanel,
          showRightPanel: apiConfig.layout.showRightPanel,
        } : undefined,
        // Logo position control
        logoPosition: apiConfig.logoPosition,
        background: {
          type: (apiConfig.backgroundType as 'gradient' | 'solid' | 'image') || 'gradient',
          value: apiConfig.backgroundValue || undefined,
          patternEnabled: apiConfig.backgroundPatternEnabled ?? true,
          patternType: apiConfig.backgroundPatternType || 'dots',
          patternOpacity: apiConfig.backgroundPatternOpacity ?? 10,
          gradientColor1: apiConfig.backgroundGradientColor1,
          gradientColor2: apiConfig.backgroundGradientColor2,
          gradientDirection: apiConfig.gradientDirection,
        },
        colors: {
          heading: apiConfig.textColorHeading,
          subheading: apiConfig.textColorSubheading,
          description: apiConfig.textColorDescription,
          featureTitle: apiConfig.textColorFeatureTitle,
          featureDescription: apiConfig.textColorFeatureDescription,
          statsLabel: apiConfig.textColorStatsLabel,
          statsValue: apiConfig.textColorStatsValue,
          featureCardBox: apiConfig.boxColorFeatureCard,
          statsContainerBox: apiConfig.boxColorStatsContainer,
          loginFormBox: apiConfig.boxColorLoginForm,
        },
        // Panel styles configuration
        styles: apiConfig.styles ? {
          leftPanel: apiConfig.styles.leftPanel,
          rightPanel: apiConfig.styles.rightPanel,
          header: apiConfig.styles.header ? { color: apiConfig.styles.header.textColor } : undefined,
          cards: apiConfig.styles.cards,
          button: apiConfig.styles.button,
          loginFormGlass: apiConfig.styles.loginFormGlass,
        } : undefined,
        // Include custom content fields
        customLogoUrl: apiConfig.customLogoUrl,
        customTitle: apiConfig.customTitle,
        customSubtitle: apiConfig.customSubtitle,
        customDescription: apiConfig.customDescription,
        customFeatureCards: apiConfig.featureCards,
        customStats: apiConfig.stats,
      }
    : {
        // Fallback to default classic template
        templateId: 'classic',
        templateName: 'Classic Split',
        description: 'Default login template',
        formPlacement: 'right',
        sections: [
          { id: 'logo', visible: true, order: 1 },
          { id: 'welcome', visible: true, order: 2 },
          { id: 'intro', visible: true, order: 3 },
          { id: 'featureCards', visible: true, order: 4 },
          { id: 'stats', visible: true, order: 5 },
          { id: 'help', visible: true, order: 7 },
          { id: 'legalLinks', visible: true, order: 8 },
          { id: 'backgroundVisual', visible: true, order: 9 },
        ],
        background: {
          type: 'gradient',
          patternEnabled: true,
        },
      };

  // Build the login form element (auth logic stays here, UI is extracted)
  const handleMfaVerify = async (mfaToken: string, otpCode: string, rm: boolean) => {
    setMfaError('');
    const result = await verifyMfa(mfaToken, otpCode, rm);
    if (!result.success) { setMfaError(result.message); return; }
    if (!result.requiresMfa && !result.requiresOtp) await doRedirect(result.user);
  };

  const handleOtpLogin = async (identifier: string, otpCode: string, pc?: string, rm?: boolean) => {
    setMfaError('');
    const result = await otpLogin(identifier, otpCode, pc, rm);
    if (!result.success) { setMfaError(result.message); return; }
    if (!result.requiresMfa && !result.requiresOtp) await doRedirect(result.user);
  };

  // Login OTP handlers
  const handleLoginOtpVerify = async (challengeId: string, otpCode: string) => {
    setMfaError('');
    const result = await verifyLoginOtp(challengeId, otpCode);
    if (!result.success) { setMfaError(result.message); return; }
    if (!result.requiresOtp && !result.requiresMfa) {
      await doRedirect(result.user);
    }
  };

  const handleLoginOtpResend = async (challengeId: string) => {
    const result = await resendLoginOtp(challengeId);
    if (result.success && result.data) {
      return result.data;
    }
    setMfaError(result.message || 'Failed to resend OTP');
    return null;
  };

  const handleLoginOtpBack = () => {
    setStep('password');
    setLoginOtpChallenge(null);
    setMfaError('');
    setError('');
  };

  // Determine which form to render based on current step
  let loginFormElement: React.ReactNode;

  if (step === 'loginOtp' && loginOtpChallenge) {
    loginFormElement = (
      <div className="p-4">
        <LoginOtpVerifyStep
          challenge={loginOtpChallenge}
          onVerify={handleLoginOtpVerify}
          onResend={handleLoginOtpResend}
          onBack={handleLoginOtpBack}
          error={mfaError}
        />
      </div>
    );
  } else if (step === 'mfa' && mfaChallenge) {
    loginFormElement = (
      <div className="p-4">
        <MfaVerifyStep
          challenge={mfaChallenge}
          rememberMe={rememberMe}
          onVerify={handleMfaVerify}
          error={mfaError}
        />
      </div>
    );
  } else if (step === 'otp') {
    loginFormElement = (
      <div className="p-4">
        {/* "BOTH" mode: show tab switcher */}
        {loginMode === 'BOTH' && (
          <div className="flex mb-4 rounded-lg overflow-hidden border border-border dark:border-border">
            <button
              type="button"
              onClick={() => { setStep('password'); setMfaError(''); }}
              className="flex-1 px-3 py-2 text-xs font-medium bg-background dark:bg-background text-primary border-r border-border dark:border-border hover:bg-muted/80 transition-colors"
            >
              Password
            </button>
            <button
              type="button"
              className="flex-1 px-3 py-2 text-xs font-medium bg-primary text-primary-foreground"
            >
              OTP
            </button>
          </div>
        )}
        <OtpLoginStep
          programCode={urlProgramCode}
          onSendOtp={sendOtp}
          onOtpLogin={handleOtpLogin}
          onBack={loginMode === 'BOTH' ? () => { setStep('password'); setMfaError(''); } : undefined}
          error={mfaError}
        />
      </div>
    );
  } else {
    loginFormElement = (
      <>
        {/* "BOTH" mode: show tab switcher */}
        {loginMode === 'BOTH' && (
          <div className="px-4 pt-4">
            <div className="flex rounded-lg overflow-hidden border border-border dark:border-border">
              <button
                type="button"
                className="flex-1 px-3 py-2 text-xs font-medium bg-primary text-primary-foreground"
              >
                Password
              </button>
              <button
                type="button"
                onClick={() => { setStep('otp'); setError(''); setMfaError(''); }}
                className="flex-1 px-3 py-2 text-xs font-medium bg-background dark:bg-background text-muted-foreground dark:text-muted-foreground hover:bg-muted/80 transition-colors"
              >
                OTP
              </button>
            </div>
          </div>
        )}
        <LoginForm
          username={username}
          password={password}
          showPassword={showPassword}
          error={error}
          loading={loading}
          isProgramLogin={isProgramLogin}
          urlProgramCode={urlProgramCode}
          onUsernameChange={setUsername}
          onPasswordChange={setPassword}
          onTogglePassword={() => setShowPassword(!showPassword)}
          onSubmit={handleSubmit}
        />
        {/* Self-Registration slot — only takes up space once it's confirmed enabled
            and applicable to this program, so an unavailable/not-yet-resolved
            option never reserves empty card height. */}
        {effectiveProgramCode && selfRegistrationVisible && (
          <div className="px-4 pb-2 pt-1" data-testid="self-registration-slot">
            <div className="border-t border-border/50 pt-3 text-center">
              <p className="text-xs text-muted-foreground mb-1.5">New patient?</p>
              <button
                type="button"
                onClick={() => setSelfRegModalOpen(true)}
                className="text-xs font-medium text-primary hover:text-primary/80 transition-colors underline underline-offset-2"
              >
                Register as a Patient
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <LoginTemplateRenderer
        config={templateConfig}
        loginForm={loginFormElement}
        themeSettings={themeSettings as unknown as LoginTemplateProps['themeSettings']}
        appThemeSettings={appThemeSettings as unknown as LoginTemplateProps['appThemeSettings']}
        isProgramLogin={isProgramLogin}
        urlProgramCode={urlProgramCode}
        legalLinks={loginLinks.map(l => ({ ...l, externalUrl: l.externalUrl ?? undefined, programId: l.programId ?? undefined }))}
      />
      {/* Self-Registration Modal */}
      {selfRegistrationVisible && effectiveProgramCode && selfRegConfig && (
        <SelfRegistrationModal
          open={selfRegModalOpen}
          onClose={() => setSelfRegModalOpen(false)}
          programCode={effectiveProgramCode}
          config={selfRegConfig}
        />
      )}
    </>
  );
}
